import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { CalendarPlus, CheckCircle2, MapPin } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

import { useCases, type CaseRow } from '@/features/cases/api'
import { localDate } from '@/lib/format'

import {
  VISIT_REASONS,
  useSubmitTripRequest,
  useSubmitWeeklyPlan,
  type PlannedStop,
  type VisitReason,
} from './api'

/**
 * Agent-side planning page. Two modes:
 *   - Ad-hoc visit request — date + start time + canonical T5 stops picked
 *     from the user's FIELD-allocated cases, with a per-stop visit reason.
 *   - Weekly plan — Monday-anchored week + raw JSON stops blob per day.
 *     Weekly plans still use the paste flow; the per-day case picker is
 *     out of T5 scope.
 */
export function PlannerPage() {
  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <AdHocTripCard />
      <WeeklyPlanCard />
    </div>
  )
}

// ---------------------------------------------------------------------------

function AdHocTripCard() {
  const today = todayISO()
  const [date, setDate] = useState(today)
  const [time, setTime] = useState('10:00')
  /** Selected cases keyed by caseId → the chosen visit reason. */
  const [selected, setSelected] = useState<Record<string, VisitReason>>({})
  const submit = useSubmitTripRequest()

  // FIELD-allocated cases for the current user. Same endpoint the queue
  // page uses; mine=true + slot=FIELD narrows to what the validator will
  // accept on submit.
  const cases = useCases({ mine: true, slot: 'FIELD', limit: 200 })

  const selectedCount = Object.keys(selected).length

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const plannedStops: PlannedStop[] = Object.entries(selected).map(
      ([caseId, visitReason]) => ({ caseId, visitReason }),
    )
    await submit.mutateAsync({
      scheduledDate: date,
      scheduledStartTime: time,
      plannedStops,
    })
  }

  function toggleCase(caseId: string) {
    setSelected((prev) => {
      const next = { ...prev }
      if (next[caseId]) delete next[caseId]
      else next[caseId] = 'COLLECTION_VISIT'
      return next
    })
  }

  function setReason(caseId: string, reason: VisitReason) {
    setSelected((prev) => ({ ...prev, [caseId]: reason }))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarPlus className="h-5 w-5" />
          Ad-hoc visit request
        </CardTitle>
        <CardDescription>
          For a single date. Routes to your mapped manager for approval — or
          auto-approves if it falls inside an approved weekly plan.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={onSubmit}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="trip-date">Scheduled date</Label>
              <Input
                id="trip-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="trip-time">Start time</Label>
              <Input
                id="trip-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                required
              />
            </div>
          </div>

          <CasePicker
            cases={cases}
            selected={selected}
            selectedCount={selectedCount}
            onToggle={toggleCase}
            onReason={setReason}
          />

          {submit.isSuccess && (
            <Alert>
              <AlertTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Submitted
              </AlertTitle>
              <AlertDescription>
                Visit request {submit.data.tripRequestId.slice(0, 8)}… is{' '}
                <Badge tone="slate">{submit.data.status}</Badge>
                {submit.data.autoApprovedFromPlan && (
                  <> — auto-approved from your weekly plan.</>
                )}
              </AlertDescription>
            </Alert>
          )}
          {submit.isError && (
            <Alert variant="destructive">
              <AlertDescription>{submitError(submit.error)}</AlertDescription>
            </Alert>
          )}
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={submit.isPending}>
              <MapPin className="mr-2 h-4 w-4" />
              Submit visit request
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------

function CasePicker({
  cases,
  selected,
  selectedCount,
  onToggle,
  onReason,
}: {
  cases: ReturnType<typeof useCases>
  selected: Record<string, VisitReason>
  selectedCount: number
  onToggle: (caseId: string) => void
  onReason: (caseId: string, reason: VisitReason) => void
}) {
  const rows = useMemo(() => cases.data ?? [], [cases.data])

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <Label>Planned stops</Label>
        {selectedCount > 0 && (
          <span className="text-xs text-muted-foreground">
            {selectedCount} selected
          </span>
        )}
      </div>
      <div className="max-h-80 overflow-y-auto rounded-md border border-slate-200 bg-slate-50/40 p-2">
        {cases.isLoading && <SkeletonRows rows={3} />}
        {!cases.isLoading && cases.isError && (
          <p className="px-2 py-4 text-xs text-negative">
            Couldn't load your cases. Submit without stops or retry.
          </p>
        )}
        {!cases.isLoading && !cases.isError && rows.length === 0 && (
          <p className="px-2 py-4 text-xs text-muted-foreground">
            No FIELD-allocated cases right now — submit empty or come back
            when your queue updates.
          </p>
        )}
        {rows.map((c) => (
          <PickerRow
            key={c.caseId}
            row={c}
            selected={Boolean(selected[c.caseId])}
            reason={selected[c.caseId]}
            onToggle={() => onToggle(c.caseId)}
            onReason={(r) => onReason(c.caseId, r)}
          />
        ))}
      </div>
    </div>
  )
}

function PickerRow({
  row,
  selected,
  reason,
  onToggle,
  onReason,
}: {
  row: CaseRow
  selected: boolean
  reason: VisitReason | undefined
  onToggle: () => void
  onReason: (r: VisitReason) => void
}) {
  return (
    <div
      className={`rounded-md border px-3 py-2 mb-2 ${
        selected
          ? 'border-navy-300 bg-white shadow-sm'
          : 'border-transparent bg-white/60 hover:bg-white'
      }`}
    >
      <label className="flex cursor-pointer items-start gap-2">
        <input
          type="checkbox"
          className="mt-1"
          checked={selected}
          onChange={onToggle}
        />
        <div className="flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-mono text-xs font-semibold text-navy-900">
              {row.loanAccountNo}
            </span>
            <span className="text-xs text-muted-foreground">
              DPD {row.currentDpd ?? '—'}
              {row.currentBucket ? ` · ${row.currentBucket}` : ''}
            </span>
          </div>
          <div className="text-xs text-slate-700">
            {row.customerName}
            {row.dncFlag && (
              <Badge tone="negative" className="ml-2">
                DNC
              </Badge>
            )}
          </div>
        </div>
      </label>
      {selected && (
        <div className="mt-2 flex flex-wrap gap-1.5 pl-6">
          {VISIT_REASONS.map((r) => (
            <Chip
              key={r.code}
              active={reason === r.code}
              onClick={() => onReason(r.code)}
            >
              {r.label}
            </Chip>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

function WeeklyPlanCard() {
  const [weekStart, setWeekStart] = useState(nextMondayISO())
  const [stopsText, setStopsText] = useState(weeklyExample)
  const submit = useSubmitWeeklyPlan()

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    let plannedStops: unknown
    try {
      plannedStops = JSON.parse(stopsText)
    } catch {
      plannedStops = {}
    }
    await submit.mutateAsync({ weekStartDate: weekStart, plannedStops })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarPlus className="h-5 w-5" />
          Weekly plan
        </CardTitle>
        <CardDescription>
          Pre-approve a Monday-anchored week. Approved plans auto-approve
          any ad-hoc visit request you submit inside that week.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={onSubmit}>
          <div>
            <Label htmlFor="plan-monday">Week starting (Monday)</Label>
            <Input
              id="plan-monday"
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              required
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Must be a Monday — the API rejects other days with 400.
            </p>
          </div>
          <div>
            <Label htmlFor="plan-stops">Planned stops per day (JSON)</Label>
            <Textarea
              id="plan-stops"
              value={stopsText}
              onChange={(e) => setStopsText(e.target.value)}
              rows={10}
              className="font-mono text-xs"
            />
          </div>
          {submit.isSuccess && (
            <Alert>
              <AlertTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Submitted
              </AlertTitle>
              <AlertDescription>
                Plan {submit.data.weeklyPlanId.slice(0, 8)}… is{' '}
                <Badge tone="slate">{submit.data.status}</Badge>.
              </AlertDescription>
            </Alert>
          )}
          {submit.isError && (
            <Alert variant="destructive">
              <AlertDescription>{submitError(submit.error)}</AlertDescription>
            </Alert>
          )}
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={submit.isPending}>
              <MapPin className="mr-2 h-4 w-4" />
              Submit weekly plan
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------

function submitError(err: unknown): string {
  if (isAxiosError(err)) {
    const status = err.response?.status
    const msg = (err.response?.data as { message?: string } | undefined)?.message
    if (status === 400) return msg ?? 'Invalid input — check fields above.'
    if (status === 409) return msg ?? 'A conflicting plan or request already exists for this period.'
    if (status === 403) return 'You are not authorised to do this.'
    return msg ?? err.message
  }
  return String(err)
}

function todayISO(): string {
  return localDate()
}

function nextMondayISO(): string {
  const d = new Date()
  // 0=Sun, 1=Mon… target Monday on or after today.
  const dow = d.getDay()
  const delta = (8 - (dow === 0 ? 7 : dow)) % 7 || 7
  d.setDate(d.getDate() + delta)
  return localDate(d)
}

const weeklyExample = JSON.stringify(
  {
    MON: [],
    TUE: [],
    WED: [],
    THU: [],
    FRI: [],
    SAT: [],
    SUN: [],
  },
  null,
  2,
)
